import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { RotateCcw, Save } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { db } from "../../firebase";
import { sanitizeForFirestore } from "../../lib/firebaseSafe";
import { ADMIN_EMAILS } from "../../lib/platformData";
import { FONT_FAMILY_OPTIONS, TYPOGRAPHY_TARGETS, typographyStyle } from "../../lib/typography";

const weights = ["300", "400", "500", "600", "700", "800", "900"];
const alignments = [
  ["", "Défaut"],
  ["left", "Gauche"],
  ["center", "Centré"],
  ["right", "Droite"],
  ["justify", "Justifié"],
];
const transforms = [
  ["", "Défaut"],
  ["none", "Normal"],
  ["uppercase", "Majuscules"],
  ["lowercase", "Minuscules"],
  ["capitalize", "Capitaliser"],
];

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-gray-400">
        {label}
      </span>
      {children}
    </label>
  );
}

function inputClass() {
  return "w-full rounded-lg border border-white/10 bg-black px-3 py-2 text-white outline-none transition focus:border-red-500";
}

export default function AdminTypography() {
  const { user, loading, loginWithGoogle, logout } = useAuth();
  const toast = useToast();
  const [typography, setTypography] = useState({});
  const [activeKey, setActiveKey] = useState(TYPOGRAPHY_TARGETS[0].key);
  const [saving, setSaving] = useState(false);
  const isAdmin = Boolean(user && ADMIN_EMAILS.includes(user.email));

  useEffect(() => {
    if (!isAdmin) return undefined;
    return onSnapshot(doc(db, "admin", "content"), (snap) => {
      const data = snap.exists() ? snap.data() : {};
      setTypography(data.typography || {});
    });
  }, [isAdmin]);

  const groupedTargets = useMemo(
    () => TYPOGRAPHY_TARGETS.reduce((acc, target) => {
      acc[target.group] = acc[target.group] || [];
      acc[target.group].push(target);
      return acc;
    }, {}),
    []
  );
  const target = TYPOGRAPHY_TARGETS.find((item) => item.key === activeKey) || TYPOGRAPHY_TARGETS[0];
  const draft = typography[activeKey] || {};

  const updateDraft = (patch) => {
    setTypography((current) => ({
      ...current,
      [activeKey]: {
        ...(current[activeKey] || {}),
        ...patch,
      },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await setDoc(
        doc(db, "admin", "content"),
        sanitizeForFirestore({
          typography,
          updatedAt: serverTimestamp(),
        }),
        { merge: true }
      );
      toast.success("Typographie sauvegardée", target.label);
    } catch (err) {
      toast.error("Sauvegarde impossible", err.message);
    } finally {
      setSaving(false);
    }
  };

  const resetTarget = () => {
    setTypography((current) => ({ ...current, [activeKey]: {} }));
    toast.success("Zone réinitialisée", "Clique sur sauvegarder pour publier.");
  };

  if (loading) return <main className="min-h-screen bg-black p-8 text-white">Chargement...</main>;

  if (!user || !isAdmin) {
    return (
      <main className="grid min-h-screen place-items-center bg-black px-6 text-white">
        <section className="max-w-md rounded-lg border border-white/10 bg-zinc-950 p-6 text-center">
          <h1 className="text-3xl font-black uppercase">Admin Typographie</h1>
          <p className="mt-3 text-gray-400">Connexion admin requise.</p>
          {user && !isAdmin ? (
            <button onClick={logout} className="mt-6 rounded-full bg-red-600 px-5 py-3 text-sm font-bold uppercase">
              Changer de compte
            </button>
          ) : (
            <button onClick={loginWithGoogle} className="mt-6 rounded-full bg-white px-5 py-3 text-sm font-bold uppercase text-black">
              Connexion Google
            </button>
          )}
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-black p-4 text-white md:p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="flex flex-col justify-between gap-4 rounded-lg border border-white/10 bg-zinc-950 p-5 md:flex-row md:items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-500">Admin design</p>
            <h1 className="mt-2 text-4xl font-black uppercase">Typographie ciblée</h1>
            <p className="mt-2 max-w-3xl text-sm text-gray-400">
              Choisis une zone précise du site, puis modifie seulement cette zone.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={resetTarget} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-bold uppercase">
              <RotateCcw size={16} /> Réinitialiser zone
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold uppercase text-black disabled:opacity-60"
            >
              <Save size={16} /> {saving ? "Sauvegarde..." : "Sauvegarder"}
            </button>
            <Link to="/" className="rounded-full border border-white/15 px-4 py-2 text-sm font-bold uppercase">
              Voir le site
            </Link>
          </div>
        </header>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
          <aside className="rounded-lg border border-white/10 bg-zinc-950 p-4">
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.22em] text-red-500">
              Zones modifiables
            </p>
            <div className="space-y-5">
              {Object.entries(groupedTargets).map(([group, targets]) => (
                <div key={group}>
                  <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">{group}</p>
                  <div className="space-y-2">
                    {targets.map((item) => (
                      <button
                        key={item.key}
                        onClick={() => setActiveKey(item.key)}
                        className={`w-full rounded-lg border px-3 py-2 text-left text-sm transition ${
                          activeKey === item.key
                            ? "border-red-500 bg-red-600/15 text-white"
                            : "border-white/10 bg-black text-gray-300 hover:border-white/30"
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </aside>

          <section className="space-y-6">
            <div className="rounded-lg border border-white/10 bg-zinc-950 p-5 md:p-6">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-500">
                {target.group}
              </p>
              <h2 className="mt-2 text-2xl font-black uppercase">{target.label}</h2>

              <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="Police">
                  <select className={inputClass()} value={draft.fontFamily || ""} onChange={(event) => updateDraft({ fontFamily: event.target.value })}>
                    {FONT_FAMILY_OPTIONS.map((font) => (
                      <option key={font.label} value={font.value}>{font.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Police personnalisée">
                  <input className={inputClass()} value={draft.customFont || ""} placeholder="Ex: Poppins, Arial, sans-serif" onChange={(event) => updateDraft({ customFont: event.target.value })} />
                </Field>
                <Field label="Taille en px">
                  <input className={inputClass()} type="number" value={draft.fontSize || ""} placeholder="laisser vide = style actuel" onChange={(event) => updateDraft({ fontSize: event.target.value })} />
                </Field>
                <Field label="Graisse">
                  <select className={inputClass()} value={draft.fontWeight || ""} onChange={(event) => updateDraft({ fontWeight: event.target.value })}>
                    <option value="">Défaut</option>
                    {weights.map((weight) => <option key={weight} value={weight}>{weight}</option>)}
                  </select>
                </Field>
                <Field label="Alignement">
                  <select className={inputClass()} value={draft.textAlign || ""} onChange={(event) => updateDraft({ textAlign: event.target.value })}>
                    {alignments.map(([value, label]) => <option key={label} value={value}>{label}</option>)}
                  </select>
                </Field>
                <Field label="Transformation">
                  <select className={inputClass()} value={draft.textTransform || ""} onChange={(event) => updateDraft({ textTransform: event.target.value })}>
                    {transforms.map(([value, label]) => <option key={label} value={value}>{label}</option>)}
                  </select>
                </Field>
                <Field label="Hauteur de ligne">
                  <input className={inputClass()} type="number" step="0.05" value={draft.lineHeight || ""} placeholder="Ex: 1.4" onChange={(event) => updateDraft({ lineHeight: event.target.value })} />
                </Field>
                <Field label="Espacement lettres px">
                  <input className={inputClass()} type="number" step="0.1" value={draft.letterSpacing ?? ""} placeholder="Ex: 0.5" onChange={(event) => updateDraft({ letterSpacing: event.target.value })} />
                </Field>
                <label className="flex items-center gap-3 rounded-lg border border-white/10 bg-black px-4 py-3 text-sm font-bold uppercase">
                  <input type="checkbox" checked={draft.italic === true} onChange={(event) => updateDraft({ italic: event.target.checked })} />
                  Italique
                </label>
              </div>
            </div>

            <div className="rounded-lg border border-white/10 bg-zinc-950 p-5 md:p-6">
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.22em] text-red-500">Aperçu</p>
              <div className="rounded-lg border border-white/10 bg-black p-6">
                <p className="text-sm text-gray-500">Zone : {target.label}</p>
                <p className="mt-4 text-4xl font-black uppercase" style={typographyStyle(draft)}>
                  Unlimited Session, direction artistique et musique premium.
                </p>
                <p className="mt-4 max-w-2xl text-gray-400" style={typographyStyle(draft)}>
                  Ceci est un aperçu du rendu typographique. Les changements restent ciblés à la zone sélectionnée.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
